// ONLY app initialization and auth - NO RE-EXPORTS!
// This file is kept minimal to enable proper tree-shaking
// Research: Synchronous re-exports force Vite to bundle entire Firebase SDK immediately

import { initializeApp } from "firebase/app";
import { initializeAuth, browserLocalPersistence } from "firebase/auth";
import { firebaseConfig, validateEnvVars } from "./config/env.js";

// Validate environment variables on startup
if (!validateEnvVars()) {
  throw new Error('Firebase initialization failed due to missing environment variables');
}

// Initialize Firebase app
const app = initializeApp(firebaseConfig);

// Lazy-loaded Analytics instance
let analyticsInstance = null;

// Function to initialize analytics only when needed
export const getAnalyticsInstance = async () => {
  if (!analyticsInstance) {
    try {
      const { getAnalytics } = await import("firebase/analytics");
      analyticsInstance = getAnalytics(app);
      console.log('Google Analytics initialized lazily with measurement ID:', firebaseConfig.measurementId);
    } catch (error) {
      console.error('Failed to initialize analytics:', error);
    }
  }
  return analyticsInstance;
};

// Initialize analytics after a delay to not block critical rendering
setTimeout(async () => {
  await getAnalyticsInstance();
}, 2000); // 2 second delay

// Utility function for tracking events (lazy loads analytics if needed)
export const trackEvent = async (eventName, parameters = {}) => {
  try {
    const analytics = await getAnalyticsInstance();
    if (analytics) {
      const { logEvent } = await import("firebase/analytics");
      logEvent(analytics, eventName, parameters);
    }
  } catch (error) {
    console.error('Failed to track event:', error);
  }
};

// Custom auth init to prevent iframe loading (saves 89.7KB)
// Using initializeAuth() instead of getAuth() prevents popup/redirect infrastructure
export const auth = initializeAuth(app, {
  persistence: browserLocalPersistence
  // CRITICAL: NO popupRedirectResolver = no 89.7KB iframe.js!
});

// Export app for other Firebase services
export default app;

// Essential auth exports only
export { onAuthStateChanged } from "firebase/auth";

// CRITICAL: NO FIRESTORE EXPORTS HERE!
// This is the key change - no synchronous re-exports that kill tree-shaking
// Components must import from firestore-lite.js or firestore-full.js instead

// Utility functions that were in the original firebase.js
function generateRandomURL(length) {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let randomURL = '';

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    randomURL += characters.charAt(randomIndex);
  }

  return randomURL;
}

function generateReferralCode() {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let referralCode = '';

  for (let i = 0; i < 10; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    referralCode += characters.charAt(randomIndex);
  }

  return referralCode;
}

// Export utility functions
export function isPasswordValid(password) {
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
  return passwordRegex.test(password);
}

export const sendVerificationEmail = async () => {
  try {
    const user = auth.currentUser;

    if (user && !user.emailVerified) {
      const { sendEmailVerification } = await import("firebase/auth");
      await sendEmailVerification(user);
    }
  } catch (error) {
    throw new Error(`Failed to send verification email: ${error.message}`);
  }
};

// Legacy utility functions - kept for backward compatibility
export async function createUserProfile(user) {
  const displayName = user.displayName || '';
  const firstName = displayName.split(' ')[0] || '';
  const lastName = displayName.split(' ')[1] || '';

  const userProfileData = {
    firstName,
    lastName,
    displayName,
    points: 100,
    referralSubmitted: false,
    hasSeenWelcomeMessage: false,
  };

  // PERFORMANCE: Use firestore-lite for CRUD operations
  const { collection, doc, setDoc, getDoc, getFirestoreLite } = await import('./firebase/firestore-lite.js');
  const db = getFirestoreLite();
  
  const userProfilesCollection = collection(db, "userProfiles");

  if (!user.customURL) {
    const uniqueRandomURL = await generateUniqueRandomURL(userProfilesCollection);
    userProfileData.customURL = uniqueRandomURL;
  } else {
    userProfileData.customURL = user.customURL;
  }

  const referralCode = generateReferralCode();
  userProfileData.referralCode = referralCode;

  const userDocRef = doc(userProfilesCollection, user.uid);

  try {
    await setDoc(userDocRef, userProfileData);
  } catch (error) {
    throw new Error(`Failed to create user profile: ${error.message}`);
  }
}

async function generateUniqueRandomURL(collectionRef) {
  let randomURL;
  let isUnique = false;

  const { doc, getDoc } = await import('./firebase/firestore-lite.js');

  while (!isUnique) {
    randomURL = generateRandomURL(8);
    const docRef = doc(collectionRef, randomURL);
    const docSnapshot = await getDoc(docRef);

    if (!docSnapshot.exists()) {
      isUnique = true;
    }
  }

  return randomURL;
}

// Configuration exports
export { firebaseConfig } from './config/env.js';
export const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY || 'BGEMYjsxouyuO5i0hHqGlibE5MCSpjYvbis5FJhnuA-g01zk4Vy9IcoTNFvz53JQjDvmLhQYrvkNK1agYiynxtE';     
export const getWebRTCConfig = () => {
  const turnUsername = import.meta.env.VITE_TURN_USERNAME || '9f24237eef08d63be9f50c85';
  const turnCredential = import.meta.env.VITE_TURN_CREDENTIAL || 'bcSTypx8qHWJ7hH9';
  const turnUrl = import.meta.env.VITE_TURN_URL || 'turn:standard.relay.metered.ca:80';

  return {
    iceServers: [
      {
        urls: [
          'stun:stun.l.google.com:19302',
          'stun:stun1.l.google.com:19302',
          'stun:stun2.l.google.com:19302'
        ]
      },
      {
        urls: turnUrl,
        username: turnUsername,
        credential: turnCredential
      }
    ]
  };
};
